import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Clock, DollarSign, CheckCircle2 } from "lucide-react";

export const PAY_STATUS = {
  pendente: { label: "Pendente", short: "Pendente", cls: "bg-red-950/50 text-red-200 border-red-800", dot: "bg-red-400", icon: Clock },
  em_pagamento: { label: "Em pagamento", short: "Em pagamento", cls: "bg-amber-950/50 text-amber-200 border-amber-700", dot: "bg-amber-400", icon: DollarSign },
  finalizado: { label: "Finalizado", short: "Finalizado", cls: "bg-emerald-950/60 text-emerald-200 border-emerald-800", dot: "bg-emerald-400", icon: CheckCircle2 },
};

export function paymentStatusMeta(s) { return PAY_STATUS[s] || PAY_STATUS.pendente; }

export function PaymentStatusDot({ status, size = "h-2.5 w-2.5", testid }) {
  const m = paymentStatusMeta(status);
  return <span data-testid={testid} title={m.label} className={`inline-block rounded-full ${size} ${m.dot}`} />;
}

export function PaymentStatusBadge({ status, source, small, testid }) {
  const m = paymentStatusMeta(status);
  const Icon = m.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase ${m.cls} ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}>
      <Icon className={small ? "h-3 w-3" : "h-3.5 w-3.5"} />{m.short}
      {source === "manual" && <span title="Alterado manualmente" className="ml-1 text-[9px] opacity-80">M</span>}
    </span>
  );
}

export function PaymentStatusControl({ order, onChanged, canEdit = true }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [choice, setChoice] = useState("");
  const current = order?.payment_status || "pendente";
  const source = order?.payment_status_source || "auto";
  const auto = order?.payment_status_auto;
  const history = order?.payment_status_history || [];

  const apply = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/payment-status`, { status, note: note.trim() || null });
      onChanged?.(data);
      toast.success(status === "auto" ? "Situação voltou para automático" : "Situação atualizada");
      setOpen(false); setNote(""); setChoice("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="payment-status-control" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-mono-caps uppercase tracking-widest text-zinc-500">Situação do pagamento</div>
          <PaymentStatusBadge status={current} source={source} testid="payment-status-badge" />
        </div>
        {canEdit && (
          <button type="button" data-testid="payment-status-toggle" onClick={() => setOpen((o) => !o)} className="text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1">Ajustar <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        )}
      </div>
      {source === "manual" && auto && auto !== current && (
        <div data-testid="payment-status-auto-hint" className="text-[11px] text-zinc-500">Cálculo automático seria: <b className="text-zinc-300">{paymentStatusMeta(auto).label}</b></div>
      )}
      {open && canEdit && (
        <div className="pt-2 border-t border-zinc-800 space-y-2">
          <div className="flex flex-wrap gap-2">
            {["pendente", "em_pagamento", "finalizado"].map((k) => (
              <button key={k} type="button" data-testid={`payment-status-set-${k}`} disabled={busy} onClick={() => setChoice(k)}
                      className={`h-8 px-3 rounded-full border text-[11px] font-bold uppercase ${choice === k ? paymentStatusMeta(k).cls : "bg-zinc-900 border-zinc-800 text-zinc-300"}`}>
                {paymentStatusMeta(k).label}
              </button>
            ))}
          </div>
          {choice && (
            <>
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-status-note" placeholder="Motivo (opcional) — ex.: cliente pagou em espécie no caixa"
                     className="h-9 w-full rounded-md bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100" />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-zinc-500">Registra quem alterou e quando.</span>
                <button type="button" data-testid="payment-status-save" disabled={busy} onClick={() => apply(choice)}
                        className="h-9 px-3 rounded-md bg-green-brand hover:bg-green-brandDark text-white text-xs font-bold uppercase">Confirmar</button>
              </div>
            </>
          )}
          {source === "manual" && (
            <button type="button" data-testid="payment-status-auto" disabled={busy} onClick={() => apply("auto")} className="w-full h-8 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-300 text-[11px] font-bold uppercase">
              Voltar para automático
            </button>
          )}
          {history.length > 0 && (
            <details data-testid="payment-status-history" className="text-[11px] text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300">Histórico de alterações ({history.length})</summary>
              <ul className="mt-1 space-y-0.5">
                {history.slice().reverse().slice(0, 8).map((h, i) => (
                  <li key={i}>{new Date(h.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })} · {h.by_name} · {paymentStatusMeta(h.from).label} → {paymentStatusMeta(h.to).label}{h.note ? ` (${h.note})` : ""}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
